import React from "react";
import AdminNavbar from "./AdminNavbar";
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AdminData from "../../backend/dataservices/AdminData";

const PAGE_SIZE = 20;

// Ordnet Audit-Events eine Farbe für die Chip-Darstellung zu.
function eventColor(event) {
    switch (event) {
        case "USER_LOGIN": return "info";
        case "CREATE": return "success";
        case "UPDATE": return "warning";
        case "DELETE": return "error";
        default: return "default";
    }
}

// Formatiert einen ISO-/DATETIME-Wert als deutsches Datum + Uhrzeit.
function formatTimestamp(value) {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
}

function AuditPage() {
    const [rows, setRows] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [selected, setSelected] = React.useState(null);

    React.useEffect(() => {
        let active = true;
        setLoading(true);
        AdminData.getAuditLog(page).then(data => {
            if (!active) return;
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            setTotal(data?.total ?? 0);
            setLoading(false);
        }).catch(() => {
            if (!active) return;
            setRows([]);
            setLoading(false);
        });
        return () => { active = false; };
    }, [page]);

    // Detail-Zeilen für den Dialog aufbauen (nur befüllte Felder anzeigen).
    function detailEntries(row) {
        if (!row) return [];
        const entries = [
            ["Zeitpunkt", formatTimestamp(row.AL_Timestamp)],
            ["Event", row.AL_Event],
            ["Kategorie", row.AL_Category],
            ["Benutzer (Vorname)", row.AL_Vorname],
            ["Benutzername", row.AL_Username],
            ["Benutzer-ID", row.AL_UserID],
            ["Tenant", row.AL_TenantName],
            ["Methode", row.AL_Method],
            ["Pfad", row.AL_Path],
            ["Status", row.AL_Status],
            ["Ziel-Tabelle", row.AL_TargetTable],
            ["Ziel-Datensatz-ID", row.AL_TargetID],
            ["IP-Adresse", row.AL_IP],
            ["User-Agent", row.AL_UserAgent],
        ];
        return entries.filter(([, v]) => v !== null && v !== undefined && v !== "");
    }

    function renderDetailsJson(details) {
        if (!details) return null;
        let obj = details;
        if (typeof details === "string") {
            try { obj = JSON.parse(details); } catch { return String(details); }
        }
        const keys = Object.keys(obj).filter(k => obj[k] !== null && obj[k] !== undefined);
        if (keys.length === 0) return null;
        return keys.map(k => (
            <TableRow key={k}>
                <TableCell sx={{fontWeight: 'bold', width: '40%', verticalAlign: 'top'}}>{k}</TableCell>
                <TableCell sx={{wordBreak: 'break-word'}}>
                    {typeof obj[k] === 'object' ? JSON.stringify(obj[k]) : String(obj[k])}
                </TableCell>
            </TableRow>
        ));
    }

    return (
        <Box>
            <AdminNavbar initValue={"/admin/audit"}/>
            <Box sx={{padding: '20px'}}>
                <Typography variant="h6" sx={{mb: 2}}>Audit-Log</Typography>

                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Paper elevation={3}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{'& th': {fontWeight: 'bold', backgroundColor: '#f5f5f5'}}}>
                                        <TableCell>Datum &amp; Zeit</TableCell>
                                        <TableCell>Benutzer</TableCell>
                                        <TableCell>Event</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{color: 'gray', py: 3}}>
                                                Keine Audit-Einträge vorhanden.
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.map(row => (
                                        <TableRow
                                            key={row.AL_ID}
                                            hover
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => setSelected(row)}
                                        >
                                            <TableCell>{formatTimestamp(row.AL_Timestamp)}</TableCell>
                                            <TableCell>{row.AL_Vorname || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={row.AL_Event} color={eventColor(row.AL_Event)} size="small"/>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                            onPageChange={(_, p) => setPage(p)}
                            labelRowsPerPage="Zeilen pro Seite:"
                            labelDisplayedRows={({from, to, count}) => `${from}–${to} von ${count}`}
                        />
                    </Paper>
                )}
            </Box>

            <Dialog open={selected !== null} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    Event-Details
                    <IconButton onClick={() => setSelected(null)} size="small">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selected && (
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {detailEntries(selected).map(([label, value]) => (
                                        <TableRow key={label}>
                                            <TableCell sx={{fontWeight: 'bold', width: '40%', verticalAlign: 'top'}}>{label}</TableCell>
                                            <TableCell sx={{wordBreak: 'break-word'}}>{String(value)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {renderDetailsJson(selected.AL_Details)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default AuditPage;
